/* eslint-disable no-continue */
/* eslint-disable object-curly-newline */
/* eslint-disable camelcase */
/** 三方登录相关逻辑 */
import { mapState } from 'vuex';
import Cookies from 'js-cookie';
import {
    getTwitterRequestToken,
    getTwitterOAuthToken,
    facebookLogin,
    googleLogin,
    appleLogin,
    twitterLogin,
    lineLogin,
    getLineAccessToken,
} from '@/api/oauth';
import { getUidV4, queryConcat, searchParams, jsonParser } from '@/lib/utils';
import Logs from '@/lib/logs';
import { OAuths, OAuthKey, Platform, OAuthError } from '@/enums';

export default {
    data() {
        return {
            // twitter1.0 的初始化配置
            twitterConfig: {},

            // 以 _ 或 $ 开头的 property 不会被 Vue 实例代理
            _platform: jsonParser(
                sessionStorage.getItem('platform') || localStorage.getItem('platform')
            ),
        };
    },
    computed: {
        ...mapState(['oauth']),
        platform: {
            get() {
                return this.$data._platform;
            },
            set(value) {
                this.$data._platform = value;
            },
        },
    },
    methods: {
        // Twitter 需要在点击时单独初始化
        async twitterInitialize(cb) {
            const tokenInfo = await getTwitterRequestToken();
            this.twitterConfig = tokenInfo;
            this.openOAuth(OAuthKey.Twitter, cb);
        },

        // 打开三方授权
        openOAuth(key) {
            const query = this.getOAuthQuery(key);

            const web_pageid = `ghome-web-${this.$route.name === 'Login' ? 'login' : 'dn2-login'}`;
            Logs.imgLog_Button({
                web_pageid,
                web_buttonid: 'open-oauth',
                web_ext_json: `${key}_state=${query.state}`,
            });

            // iframe 整个窗口跳到 GHome 中进行登录（还是会有 session 共享问题 ╮(╯﹏╰）╭ ）
            if (this.platform === Platform.Iframe) {
                if (!this.$route.query.back_url) return this.$toast('No back_url parameter');

                const { back_url, lang } = this.$route.query;
                const parameters = {
                    oauthUrl: OAuths[key].url + query,
                    backUrl: back_url,
                    lang: lang || Cookies.get('lang'),
                };

                // 把 session 也带过去
                for (const attr in sessionStorage) {
                    if (Object.hasOwnProperty.call(sessionStorage, attr)) {
                        if (attr === 'platform') continue; // 该字段在 iframe 页面中处理
                        parameters[attr] = JSON.parse(sessionStorage[attr] || '""');
                    }
                }

                // prettier-ignore
                window.top.location.href = `${window.location.origin}/iframe${queryConcat(parameters)}`;
            } else {
                window.location.href = OAuths[key].url + query;
            }
        },

        // 准备对应的 OAuth URL 配置
        getOAuthQuery(key) {
            const query = { state: getUidV4() };
            switch (key) {
                case OAuthKey.Google:
                    return queryConcat({
                        ...query,
                        client_id: this.oauth.query?.googleClientId,
                        redirect_uri: this.oauth.query?.googleRedirectUrl,
                        response_type: 'token',
                        scope: 'https://www.googleapis.com/auth/userinfo.profile',
                        prompt: 'select_account',
                    });
                case OAuthKey.Facebook:
                    return queryConcat({
                        ...query,
                        client_id: this.oauth.query?.facebookClientId,
                        redirect_uri: this.oauth.query?.facebookRedirectUrl,
                        response_type: 'token',
                    });
                case OAuthKey.Apple:
                    return queryConcat({
                        ...query,
                        client_id: this.oauth.query?.appleClientId,
                        redirect_uri: this.oauth.query?.appleRedirectUrl,
                        response_type: 'code id_token',
                        response_mode: 'fragment',
                    });
                case OAuthKey.Line:
                    return queryConcat({
                        ...query,
                        client_id: this.oauth.query?.lineClientId,
                        redirect_uri: this.oauth.query?.lineRedirectUrl,
                        response_type: 'code',
                    });
                case OAuthKey.Twitter: {
                    const { requestToken } = this.twitterConfig;
                    return queryConcat({ oauth_token: requestToken });
                }
                default:
                    break;
            }
        },

        // 获取三方登录成功返回的token
        async getAccessToken(key) {
            switch (key) {
                case OAuthKey.Google: {
                    // google 返回的是 #hash 格式的 Fragment 片段
                    const { access_token: googleToken } = searchParams(
                        this.$route.hash,
                        'access_token'
                    );
                    return { googleToken };
                }
                case OAuthKey.Facebook: {
                    const { access_token: fbToken } = searchParams(
                        this.$route.hash,
                        'access_token'
                    );
                    return { fbToken };
                }
                case OAuthKey.Apple: {
                    const { code: appleUserId, id_token: appleToken } = searchParams(
                        this.$route.hash,
                        ['code', 'id_token']
                    );
                    return { appleUserId, appleToken };
                }
                case OAuthKey.Line: {
                    const { accessToken: lineToken } = await getLineAccessToken({
                        code: this.$route.query.code,
                    });
                    return { lineToken };
                }
                case OAuthKey.Twitter: {
                    const { oauth_token, oauth_verifier } = this.$route.query;
                    // prettier-ignore
                    const { accessToken, accessTokenSecret, railAccessTokenRes } = await getTwitterOAuthToken({
                        requestToken: oauth_token,
                        oauthVerifier: oauth_verifier,
                    });

                    // Twitter 不允许重复登录
                    const { TwitterRepeatLogin: repeatLogin } = OAuthError;
                    if (railAccessTokenRes === repeatLogin.msg) {
                        return Promise.reject(repeatLogin);
                    }

                    const { user_id: userId } = searchParams(`?${railAccessTokenRes}`, 'user_id');
                    return {
                        accessToken,
                        accessTokenSecret,
                        userId,
                        railAccessTokenRes,
                    };
                }
                default:
                    return {};
            }
        },

        // 处理三方登录失败返回的 error
        errorHandler(key) {
            let error = '';
            switch (key) {
                case OAuthKey.Google:
                case OAuthKey.Apple:
                    error = searchParams(this.$route.hash, 'error');
                    break;
                case OAuthKey.Facebook: {
                    const { error_description } = searchParams(
                        this.$route.hash,
                        'error_description'
                    );
                    error = error_description;
                    break;
                }
                case OAuthKey.Line:
                    error = this.$route.query.error_description;
                    break;
                case OAuthKey.Twitter:
                    // twitter has no error message
                    error = 'OAuth failed';
                    break;
                default:
                    break;
            }
            return { error };
        },

        // OAuth 后端登录
        async loginByOAuth(key, tokenInfo, config) {
            let response = {};
            switch (key) {
                case OAuthKey.Facebook:
                    response = await facebookLogin(tokenInfo, config);
                    break;
                case OAuthKey.Google:
                    response = await googleLogin(tokenInfo, config);
                    break;
                case OAuthKey.Apple:
                    response = await appleLogin(tokenInfo, config);
                    break;
                case OAuthKey.Line:
                    response = await lineLogin(tokenInfo, config);
                    break;
                case OAuthKey.Twitter:
                    response = await twitterLogin(tokenInfo, config);
                    break;
                default:
                    break;
            }
            return response;
        },
    },
};
